import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { UserActionType } from "../../reducers/user.reducer";
import { ConfirmationDialog } from "../Dialogs/ConfirmationDialog";
import { useIdleTimer } from "react-idle-timer";
import { logout, sessionTimeout } from "../../api/user";

type Props = {
    authenticated: boolean;
};

const SessionTimeout: React.FC<Props> = ({ authenticated }) => {
    const timeout: number = Number(process.env.REACT_APP_SESSION_TIME_IDLE);
    const promptBeforeIdle: number = Number(
        process.env.REACT_APP_SESSION_TIME_WARNING
    );

    const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    const dispatch = useDispatch();

    const remainLoggedIn = () => {
        activate();
        setDialogOpen(false);
    };

    // Log out the user and navigate home
    const logoutUser = (isTimeout = false) => {
        const logoutPromise = isTimeout ? sessionTimeout() : logout();

        logoutPromise
            .then(() => {
                // Token is already removed in the API functions
                dispatch({ type: UserActionType.USER_LOGOUT });
            })
            .catch(error => {
                console.error(`Error during ${isTimeout ? 'session timeout' : 'logout'}:`, error);
                // Even if the API call fails, we should log out the user locally
                localStorage.removeItem("ACCESS_TOKEN");
                dispatch({ type: UserActionType.USER_LOGOUT });
            })
            .finally(() => {
                setDialogOpen(false);
            });
    };

    const onIdle = () => {
        logoutUser(true);
    };

    const onPrompt = () => {
        setDialogOpen(true);
    };

    const { getRemainingTime, start, pause, activate } = useIdleTimer({
        onIdle,
        onPrompt,
        timeout,
        promptBeforeIdle,
        startManually: true,
        stopOnIdle: true,
        events: [
            "click",
            "MSPointerDown",
            "mousedown",
            "load",
            "focus",
            "blur",
        ],
    });

    const timeMeasure =
        secondsRemaining > 60
            ? "minutes"
            : secondsRemaining > 1
                ? "seconds"
                : "second";
    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsRemaining(Math.ceil(getRemainingTime() / 1000));
        });

        if (authenticated) start();
        else pause();
        return () => {
            clearInterval(interval);
        };
    }, [authenticated, getRemainingTime, start, pause]);

    return (
        <ConfirmationDialog
            open={dialogOpen}
            title="Session Logout"
            contentText={`You will be logged out in ${secondsRemaining > 60
                ? Math.ceil(secondsRemaining / 60)
                : secondsRemaining
                } ${timeMeasure}. Would you like to continue your current session?`}
            handleNo={() => logoutUser(false)}
            handleYes={remainLoggedIn}
        />
    );
};

export default SessionTimeout;